/**
 * Paged list queries and their facet counts.
 *
 * @neo4j/graphql v5 puts `offset` in `options` on the plain field while
 * `totalCount` lives only on the connection, so a page-number pager needs
 * both root fields in one document. Both take the same `$where` so the
 * count describes the filtered set and not the whole label.
 */

import { graphql } from '../gql';

export const TENDER_ROWS = graphql(`
  query TenderRows($limit: Int!, $offset: Int!, $where: TenderWhere, $sort: [TenderSort!]) {
    tenders(where: $where, options: { limit: $limit, offset: $offset, sort: $sort }) {
      tender_id
      title
      procuring_entity
      publication_date
      awarded_amount
      estimated_value
      value_bgn
      currency
      cpv_code
      cpv_description
      procedure_type
      winner_count
      lot_count
      offers_count
      is_eu_funded
      commissionedBy { key name }
      procuredBy { uic name portfolio }
      winners { uic name }
    }
    tendersAggregate(where: $where) { count }
  }
`);

export const TENDER_FACETS = graphql(`
  query TenderFacetCounts($filter: TenderFilter) {
    tenderFacets(filter: $filter) { key values { value count } }
  }
`);

export const COMPANY_ROWS = graphql(`
  query CompanyRows($limit: Int!, $offset: Int!, $where: CompanyWhere, $sort: [CompanySort!]) {
    companies(where: $where, options: { limit: $limit, offset: $offset, sort: $sort }) {
      uic
      name
      legal_form
      seatedIn { key name }
      tendersWonAggregate { count }
      subsidiesAggregate { count }
      taxDebtsAggregate { count }
      treasuryPayments { amount payment_count }
    }
    companiesAggregate(where: $where) { count }
  }
`);

export const COMPANY_FACETS = graphql(`
  query CompanyFacetCounts($filter: CompanyFilter) {
    companyFacets(filter: $filter) { key values { value count } }
  }
`);

export const PERSON_ROWS = graphql(`
  query PersonRows($limit: Int!, $offset: Int!, $where: PersonWhere, $sort: [PersonSort!]) {
    people(where: $where, options: { limit: $limit, offset: $offset, sort: $sort }) {
      person_hash
      name
      company_links
      ownedCompaniesAggregate { count }
      managedCompaniesAggregate { count }
      mayorOf { key name }
      # Elected office below mayor. The role is on the edge, not the node,
      # so a councillor and a village mayor are the same relationship type
      # distinguished by what it says.
      positionsConnection(sort: [{ edge: { elected_year: DESC } }], first: 4) {
        edges { properties { role area elected_year } node { key name } }
      }
      nominatedBy { key name }
    }
    peopleAggregate(where: $where) { count }
  }
`);

export const MUNICIPALITY_ROWS = graphql(`
  query MunicipalityRows($limit: Int!, $offset: Int!, $where: MunicipalityWhere, $sort: [MunicipalitySort!]) {
    municipalities(where: $where, options: { limit: $limit, offset: $offset, sort: $sort }) {
      key
      name
      province
      population_permanent
      tender_total_bgn
      spend_per_capita
      tendersAggregate { count }
      companiesAggregate { count }
      mayorsConnection {
        edges {
          properties { elected_year term_end elected_round }
          node {
            person_hash
            name
            nominatedByConnection {
              edges { properties { election_year } node { key name } }
            }
          }
        }
      }
    }
    municipalitiesAggregate(where: $where) { count }
  }
`);

export const SUBSIDY_ROWS = graphql(`
  query SubsidyRows($limit: Int!, $offset: Int!, $where: SubsidyWhere, $sort: [SubsidySort!]) {
    subsidies(where: $where, options: { limit: $limit, offset: $offset, sort: $sort }) {
      subsidy_id
      measure
      project_title
      amount
      # What ИСУН reports as actually disbursed, where it reports it at
      # all. Different from the contracted grant in kind, not only in
      # size: a project under way has a grant and a fraction of it paid.
      paid_amount
      status
      year
      source
      beneficiaries { uic name }
    }
    subsidiesAggregate(where: $where) { count }
  }
`);

export const PERSON_FACETS = graphql(`
  query PersonFacetCounts($filter: PersonFilter) {
    personFacets(filter: $filter) { key values { value count } }
  }
`);

export const MUNICIPALITY_FACETS = graphql(`
  query MunicipalityFacetCounts($filter: MunicipalityFilter) {
    municipalityFacets(filter: $filter) { key values { value count } }
  }
`);

export const SUBSIDY_FACETS = graphql(`
  query SubsidyFacetCounts($filter: SubsidyFilter) {
    subsidyFacets(filter: $filter) { key values { value count } }
  }
`);
